import { useEffect, useMemo, useState } from 'react'
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded'
import PauseRoundedIcon from '@mui/icons-material/PauseRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import { AppDialog } from '../Common/AppDialog'
import { Button } from '../Common'
import { useToast } from '../../context/useToast'
import { useWorkspace } from '../../context/useWorkspace'
import { CreditBalance } from '../Credits/CreditBalance'
import { formatCredits as formatCreditsShared } from '../Credits/formatCredits'
import { useWorkspaceWalletBalance } from '../../hooks/useCreditBalances'
import {
  executeMemberAutomationStarter,
  getMemberStarterCost,
  subscribeMemberAutomationStarters,
  type MemberAutomationStarterAction,
  type MemberAutomationStarterState,
} from '../../services/memberControlsService'
import { isNoSignInPreviewActive } from '../../debug'
import { testingOverrideAllowsAllFeatures } from '../../config/testingOverrides'
import type { WorkspaceRole } from '../../context/WorkspaceContext'
import { roleCanPerform } from '../../utils/workspacePermissions'
import { buildMemberPath } from '../../member/memberAppRouting'
import { MEMBER_QUICK_ACTIONS } from './AutomationLauncher.helpers'

export const AutomationLauncher = ({ role }: { role: WorkspaceRole }) => {
  const { activeWorkspaceId, workspaces = [] } = useWorkspace()
  const { showToast } = useToast()
  const wallet = useWorkspaceWalletBalance(activeWorkspaceId, isNoSignInPreviewActive() ? 5000 : 0, isNoSignInPreviewActive())
  const [states, setStates] = useState<MemberAutomationStarterState[]>([])
  const [pendingAction, setPendingAction] = useState<MemberAutomationStarterAction | null>(null)
  const [runningAction, setRunningAction] = useState<MemberAutomationStarterAction | null>(null)
  const canManage = roleCanPerform(role, 'EDITOR') || testingOverrideAllowsAllFeatures()

  useEffect(() => {
    if (!activeWorkspaceId) return
    return subscribeMemberAutomationStarters(activeWorkspaceId, setStates, (error) => showToast(error.message, 'error'))
  }, [activeWorkspaceId, showToast])

  const stateByAction = useMemo(() => new Map(states.map((entry) => [entry.actionType, entry])), [states])

  const run = async (actionType: MemberAutomationStarterAction, mode: 'START' | 'STOP' = 'START') => {
    if (actionType === 'INVITE_MEMBER') {
      const activeWs = workspaces.find((w) => w.id === activeWorkspaceId)
      const target = activeWs || activeWorkspaceId || ''
      window.history.pushState(null, '', buildMemberPath({ workspace: target, tab: 'team' }))
      window.dispatchEvent(new PopStateEvent('popstate'))
      return
    }
    if (!activeWorkspaceId) return
    setRunningAction(actionType)
    try {
      const result = await executeMemberAutomationStarter({ workspaceId: activeWorkspaceId, actionType, mode })
      showToast(`${result.actionType.replaceAll('_', ' ').toLocaleLowerCase()} ${result.status.toLocaleLowerCase()}.`, 'success')
      setPendingAction(null)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Automation starter failed.', 'error')
    } finally {
      setRunningAction(null)
    }
  }

  const requestStart = (actionType: MemberAutomationStarterAction) => {
    const cost = getMemberStarterCost(actionType)
    if (cost > 0) setPendingAction(actionType)
    else void run(actionType)
  }

  const pendingCost = pendingAction ? getMemberStarterCost(pendingAction) : 0
  const pendingTitle = pendingAction ? MEMBER_QUICK_ACTIONS.find((entry) => entry.id === pendingAction)?.title ?? pendingAction : ''

  const automations = MEMBER_QUICK_ACTIONS.filter((action) => action.kind === 'automation')
  const shortcuts = MEMBER_QUICK_ACTIONS.filter((action) => action.kind === 'shortcut')

  const renderCard = (action: (typeof MEMBER_QUICK_ACTIONS)[number]) => {
    const state = stateByAction.get(action.id)
    const isActive = state?.status === 'ACTIVE'
    const cost = getMemberStarterCost(action.id)
    return (
      <article key={action.id} className={`memberControlCard ${isActive ? 'isActive' : ''} memberControlCard--${action.kind}`}>
        {action.icon}
        <strong>{action.title}</strong>
        <p>{action.description}</p>
        <footer>
          {/* §8.5.9 — shortcuts don't have run-state or a cost; label them
              plainly so users don't think they burn credits. */}
          {action.kind === 'shortcut' ? (
            <span>Shortcut</span>
          ) : (
            <span>{isActive ? 'Active' : 'Idle'}{cost > 0 ? ` · ${cost} Cr` : ''}</span>
          )}
          {action.kind === 'shortcut' ? (
            <Button variant="secondary" size="sm" onClick={() => requestStart(action.id)}><GroupAddRoundedIcon /> Open</Button>
          ) : isActive ? (
            <Button variant="secondary" size="sm" disabled={!canManage || runningAction !== null} onClick={() => void run(action.id, 'STOP')}><PauseRoundedIcon /> Stop</Button>
          ) : (
            <Button variant="secondary" size="sm" disabled={!canManage || runningAction !== null} onClick={() => requestStart(action.id)}><PlayArrowRoundedIcon /> Start</Button>
          )}
        </footer>
      </article>
    )
  }

  return (
    <section className="memberControlPanel" data-testid="member-automation-launcher">
      <header className="memberControlHeader">
        <div>
          <span className="memberEyebrow">Automations</span>
          <h2>Quick starters</h2>
        </div>
      </header>
      <div className="memberControlGrid">
        {automations.map(renderCard)}
      </div>
      {shortcuts.length ? (
        <>
          <header className="memberControlHeader memberControlHeader--secondary">
            <div>
              <span className="memberEyebrow">Shortcuts</span>
              <h2>Handy links</h2>
            </div>
          </header>
          <div className="memberControlGrid">
            {shortcuts.map(renderCard)}
          </div>
        </>
      ) : null}
      {!canManage ? <p className="memberControlNote">Automation starters are available to Admins and Editors.</p> : null}
      <AppDialog
        open={Boolean(pendingAction)}
        title="Approve automation starter"
        message={pendingAction ? (
          <div className="memberConfirmStack">
            <p>{pendingTitle} will reserve and commit workspace credits through the trusted callable.</p>
            <dl>
              <div><dt>Estimated cost</dt><dd>{formatCreditsShared(pendingCost, { suffix: 'credits' })}</dd></div>
              <div><dt>Wallet balance</dt><dd><CreditBalance value={wallet.availableCredits} variant="bare" scope="workspace" label={null} /> credits</dd></div>
            </dl>
          </div>
        ) : null}
        onDismiss={() => runningAction ? undefined : setPendingAction(null)}
        actions={[
          { label: 'Cancel', onClick: () => setPendingAction(null), disabled: runningAction !== null },
          { label: runningAction ? 'Starting...' : 'Approve', variant: 'primary', autoFocus: true, disabled: runningAction !== null, loading: runningAction !== null, onClick: () => pendingAction ? run(pendingAction) : undefined },
        ]}
      />
    </section>
  )
}
